import { useState } from "react";
import { AnimatedPage } from "../../../../components";
import { ConfirmDialog } from "../../../../components/ui/confirm-dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../../components/ui/tabs";
import { useToast } from "../../../../hooks/use-toast";
import { config } from "../../../../utils/config";
import { BloquearDialog, BloquearModo } from "./components/bloquear-dialog";
import { BloqueiosTab } from "./components/bloqueios-tab";
import { EditDialog } from "./components/edit-dialog";
import { ResultsTable } from "./components/results-table";
import { SearchForm } from "./components/search-form";
import { mensagemErro, useBloqueioMutations } from "./hooks/use-bloqueios";
import { useDocumentEdit } from "./hooks/use-document-edit";
import { CadastroDocument } from "./types";
import { formatDocumento } from "./utils/bloqueio-utils";

export default function DocumentEdit() {
  const fundName = `xama_${config.stage}`;
  const { toast } = useToast();

  const {
    cadastroData,
    allOps,
    searchForm,
    setSearchForm,
    editingDocument,
    editForm,
    setEditForm,
    handleSearch,
    handleEdit,
    handleUpdate,
    handleCancelEdit,
    isSearching,
    isUpdating,
    refreshSearch,
  } = useDocumentEdit({ fundName });

  const [bloquearModo, setBloquearModo] = useState<BloquearModo | null>(null);
  const [desbloqueando, setDesbloqueando] = useState<CadastroDocument | null>(
    null,
  );
  const { desbloquear } = useBloqueioMutations(refreshSearch);

  const documentoDe = (doc: CadastroDocument) => doc.cnpj || doc.cpf || "";

  const confirmarDesbloqueio = async () => {
    if (!desbloqueando?.bloqueio_id) return;
    try {
      await desbloquear.mutateAsync({ ids: [desbloqueando.bloqueio_id] });
      toast({ title: "Bloqueio removido" });
    } catch (e) {
      toast({
        title: "Erro",
        description: mensagemErro(e),
        variant: "destructive",
      });
    }
  };

  const validateSearchForm = () => {
    if (!searchForm.telefone.trim() && !searchForm.cpfCnpj.trim()) {
      toast({
        title: "Campos obrigatórios",
        description: "Informe pelo menos um campo: Telefone ou CPF/CNPJ",
        variant: "destructive",
      });
      return false;
    }
    return true;
  };

  const handleSearchClick = () => {
    if (!validateSearchForm()) return;
    handleSearch();
  };

  const handleSaveClick = () => {
    handleUpdate();
    toast({
      title: "Cadastro atualizado",
      description: "As alterações foram salvas com sucesso.",
    });
  };

  return (
    <AnimatedPage className="p-6">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Editar Documentos
            </h1>
            <p className="text-muted-foreground mt-1">
              Buscar e editar cadastros, bloquear entregadores e desativar
              telefones
            </p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="cadastros">
        <TabsList className="mb-6">
          <TabsTrigger value="cadastros">Cadastros</TabsTrigger>
          <TabsTrigger value="bloqueios">Bloqueios</TabsTrigger>
        </TabsList>

        <TabsContent value="bloqueios">
          <BloqueiosTab onChange={refreshSearch} />
        </TabsContent>

        <TabsContent value="cadastros">
          <div className="space-y-6">
            <SearchForm
              searchForm={searchForm}
              onSearchFormChange={setSearchForm}
              onSearch={handleSearchClick}
              isSearching={isSearching}
              fundName={fundName}
            />

            <ResultsTable
              documents={cadastroData}
              onEdit={handleEdit}
              onBloquearDocumento={(doc) =>
                setBloquearModo({
                  tipo: "documento",
                  documento: documentoDe(doc),
                  ops: doc.ops || "",
                })
              }
              onDesativarTelefone={(doc) =>
                setBloquearModo({
                  tipo: "telefone",
                  documento: documentoDe(doc),
                  ops: doc.ops || "",
                  telefone: doc.telefone || "",
                })
              }
              onDesbloquear={setDesbloqueando}
            />

            <EditDialog
              open={!!editingDocument}
              document={editingDocument}
              formData={editForm}
              allOps={allOps}
              onFormChange={setEditForm}
              onSave={handleSaveClick}
              onCancel={handleCancelEdit}
              isSaving={isUpdating}
            />

            <BloquearDialog
              modo={bloquearModo}
              onClose={() => setBloquearModo(null)}
              onDone={refreshSearch}
            />

            <ConfirmDialog
              open={!!desbloqueando}
              onOpenChange={(open) => !open && setDesbloqueando(null)}
              onConfirm={confirmarDesbloqueio}
              title="Remover bloqueio?"
              description={
                desbloqueando
                  ? desbloqueando.bloqueio_telefone
                    ? `Reativa o telefone ${desbloqueando.bloqueio_telefone} de ${formatDocumento(documentoDe(desbloqueando))} na ops ${desbloqueando.ops}.`
                    : `Remove o bloqueio de ${formatDocumento(documentoDe(desbloqueando))} na ops ${desbloqueando.ops} (bloqueio do ${desbloqueando.bloqueio_origem}). Vale para todos os cadastros dessa ops.`
                  : undefined
              }
              confirmText="Desbloquear"
              cancelText="Cancelar"
            />
          </div>
        </TabsContent>
      </Tabs>
    </AnimatedPage>
  );
}
